import React from 'react';
import { Link } from 'react-router-dom';
import { CORE_SERVICES } from '../mock';
import { useI18n } from '../context/I18nContext';

export default function ServicesGrid() {
  const { t } = useI18n();
  return (
    <section id="services" className="py-12 sm:py-14 md:py-20 bg-white">
      <div className="zh-container">
        {/* Title + underline — responsive ramp so phones don't get a 3xl
            heading blowing past the viewport edge. */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-wide text-gray-900 uppercase mb-3 sm:mb-4">
            {t.services.title}
          </h2>
          <div className="h-1.5 w-20 sm:w-24 rounded-full bg-[#f5a623]" />
        </div>

        {/*
          1 col on phones (<640px), 2 on sm tablets, 3 on md+ so the tiles
          always breathe instead of jumping from 1→3 at md.
          Gap ramps from 4 → 6 → 8.
        */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {CORE_SERVICES.map((svc) => {
            const inner = (
              <>
                {/*
                  Use aspect-ratio (4/3) instead of fixed pixel heights so
                  the tile scales proportionally with the container width.
                  Fixed heights caused the image to letter-box on wider
                  columns and crop too aggressively on narrow columns.
                */}
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-md group-hover:shadow-xl transition-shadow">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    loading="lazy"
                    decoding="async"
                    // srcset picks the right resolution per viewport — phones
                    // never pull the 1200w master when 480w is enough.
                    // Only applies to Unsplash URLs; customer-asset PNGs are
                    // unchanged (they're already served from our CDN).
                    {...(svc.image?.includes('images.unsplash.com')
                      ? {
                          srcSet: [
                            `${svc.image.replace(/w=\d+/, 'w=480')} 480w`,
                            `${svc.image.replace(/w=\d+/, 'w=800')} 800w`,
                            `${svc.image.replace(/w=\d+/, 'w=1200')} 1200w`,
                          ].join(', '),
                          sizes:
                            '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw',
                        }
                      : {})}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="text-center mt-4 sm:mt-5">
                  <span className="inline-block text-[#f5a623] text-[13px] sm:text-sm md:text-[15px] font-extrabold tracking-[0.2em] sm:tracking-[0.25em] uppercase group-hover:text-[#d98c14] transition-colors">
                    {svc.title}
                  </span>
                </div>
              </>
            );
            const testId = `home-services-card-${svc.title.toLowerCase().replace(/\s+/g, '-')}`;
            return svc.external ? (
              <a
                key={svc.title}
                href={svc.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testId}
                className="group block"
              >{inner}</a>
            ) : (
              <Link
                key={svc.title}
                to={svc.href}
                data-testid={testId}
                className="group block"
              >{inner}</Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
